import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { looksLikeEmail } from '../../utils/superAdmin';

/**
 * "Forgot password?" under a sign-in form (plan 16). Uses the email typed in
 * the form. For privacy it always says "if an account exists, we sent a link",
 * so nobody can find out which emails have accounts.
 */
export default function ForgotPassword({ email }: { email: string }) {
  const { t, i18n } = useTranslation();
  const { resetPassword } = useAuth();
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'needEmail' | 'tooMany' | 'failed'>('idle');

  const send = async () => {
    if (!looksLikeEmail(email)) {
      setState('needEmail');
      return;
    }
    setState('busy');
    try {
      await resetPassword(email, i18n.language);
      setState('sent');
    } catch (err) {
      const code = (err as { code?: string } | undefined)?.code ?? '';
      setState(
        code === 'functions/resource-exhausted' ? 'tooMany' : code === 'functions/invalid-argument' ? 'needEmail' : 'failed'
      );
    }
  };

  return (
    <div className="text-center space-y-1">
      <button
        type="button"
        onClick={send}
        disabled={state === 'busy'}
        className="text-sm text-ink underline min-h-[44px] disabled:opacity-50"
      >
        {t('auth.forgot.button')}
      </button>
      {state === 'sent' && (
        <p role="status" className="text-sm text-ink">
          {t('auth.forgot.sent', { email: email.trim() })}
        </p>
      )}
      {state === 'needEmail' && <p className="text-sm text-danger-600">{t('auth.forgot.needEmail')}</p>}
      {state === 'tooMany' && <p className="text-sm text-danger-600">{t('auth.forgot.tooMany')}</p>}
      {state === 'failed' && <p className="text-sm text-danger-600">{t('auth.forgot.failed')}</p>}
    </div>
  );
}
